// Profit Sharing — works out cash profit for a month or quarter and each
// person's share.
//
//   Payments received (deposits)  −  business money out (Monthly Bank Summary)
//   = cash profit  ×  each person's %  = payout
//
// Owner draws (personal) and transfers don't reduce profit. Owner reimbursable
// does. Warns when a business account has no bank summary for a month in the
// period, or when payments in the period look duplicated.
//
// Priority:
//   Money in is ONLY recorded payments (deposits). Approved Invoices' "Paid"
//   button creates those deposits, so a paid invoice is already in money in
//   through its deposit and is never added again. Paid invoices with no
//   deposit are listed as a warning (not counted) so the payment gets recorded.
//   Payroll comes out through the bank statement payroll lines. Tech Daily Pay
//   (dailyLabor) is shown next to it as a reconciliation line only — it is
//   NOT subtracted from profit a second time.
//
// Firestore:
//   reads   deposits, bankMonthlySummaries, bankStatementEntries,
//           bankStatementAccounts, users, approvedInvoices, dailyLabor,
//           appSettings/bankSpendCategories
//   writes  appSettings/profitSharing   { participants: [{ userId, name, percent }] }
//           profitSharingPayouts        one saved snapshot per recorded payout

import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, getDoc, doc, setDoc, addDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebaseConfig";
import "./FinancePages.css";
import * as XLSX from "xlsx";
import { formatMoney, formatDateMDY } from "../utils/financeEntries";
import { mergeCategories, loadCustomCategories, KINDS } from "../utils/bankSpendCategories";
import { computeProfit, computePayouts, periodLabel, periodKey, summariesFromEntries, combineSummaries, reimbursableActivity } from "../utils/profitCalc";
import { findDuplicateGroups } from "../utils/depositDuplicates";
import { fetchDailyLaborDays, summarizeDailyLabor, usersToMap } from "../utils/techDailyPay";
import { monthsRange, payrollFromCategoryTotals, paidInvoicesWithoutDeposit } from "../utils/profitLaborCheck";

const now = new Date();
const defaultMonth = () => {
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const monthShort = (m) => {
  const [y, mo] = m.split("-").map(Number);
  return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
};
const accountLabel = (a) =>
  [a.nickname || a.bankName, a.accountLast4 ? `••${a.accountLast4}` : ""].filter(Boolean).join(" ");
const userName = (u) => `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Unnamed";

export default function ProfitSharing() {
  const [periodType, setPeriodType] = useState("month");
  const [month, setMonth] = useState(defaultMonth());
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState(Math.max(1, Math.ceil((now.getMonth() + 1) / 3) - 1) || 1);

  const [deposits, setDeposits] = useState([]);
  const [manualSummaries, setManualSummaries] = useState([]);
  const [bankEntries, setBankEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [customCats, setCustomCats] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [savedParticipants, setSavedParticipants] = useState("[]");
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addUserId, setAddUserId] = useState("");
  const [busy, setBusy] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [laborDays, setLaborDays] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [depSnap, sumSnap, entrySnap, accSnap, userSnap, custom, psSnap, histSnap] = await Promise.all([
          getDocs(collection(db, "deposits")),
          getDocs(collection(db, "bankMonthlySummaries")),
          getDocs(collection(db, "bankStatementEntries")),
          getDocs(collection(db, "bankStatementAccounts")),
          getDocs(collection(db, "users")),
          loadCustomCategories(),
          getDoc(doc(db, "appSettings", "profitSharing")),
          getDocs(collection(db, "profitSharingPayouts")),
        ]);
        setDeposits(depSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setManualSummaries(sumSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setBankEntries(entrySnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setAccounts(accSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setUsers(userSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => userName(a).localeCompare(userName(b))));
        setCustomCats(custom);
        // Informational only (never added to / subtracted from profit).
        Promise.all([
          getDocs(collection(db, "approvedInvoices")).then((snap) => setInvoices(snap.docs.map((d) => ({ id: d.id, ...d.data() })))),
          fetchDailyLaborDays().then(setLaborDays),
        ]).catch((err) => console.warn("Could not load invoices / Tech Daily Pay for the checks:", err));
        const p = psSnap.exists() ? psSnap.data().participants || [] : [];
        setParticipants(p);
        setSavedParticipants(JSON.stringify(p));
        setPayoutHistory(
          histSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => String(b.periodKey).localeCompare(String(a.periodKey)))
        );
      } catch (e) {
        console.error("Error loading profit sharing data", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const categories = useMemo(() => mergeCategories(customCats), [customCats]);
  // Uploaded statement months replace typed Monthly Summaries for the same account + month.
  const summaries = useMemo(
    () => combineSummaries(manualSummaries, summariesFromEntries(bankEntries, accounts)),
    [manualSummaries, bankEntries, accounts]
  );
  const period = useMemo(
    () =>
      periodType === "year"
        ? { type: "year", year: Number(year) }
        : periodType === "quarter"
        ? { type: "quarter", year: Number(year), quarter: Number(quarter) }
        : { type: "month", month },
    [periodType, year, quarter, month]
  );

  const result = useMemo(
    () => computeProfit({ period, deposits, summaries, accounts, categories }),
    [period, deposits, summaries, accounts, categories]
  );
  const payouts = useMemo(() => computePayouts(result.cashProfit, participants), [result.cashProfit, participants]);

  // Month-by-month for quarters and years.
  const monthly = useMemo(
    () =>
      result.months.map((m) => {
        const r = computeProfit({ period: { type: "month", month: m }, deposits, summaries, accounts, categories });
        return { month: m, r, future: r.finishedMonths.length === 0, bankOk: !r.noAccounts && r.missing.length === 0 };
      }),
    [result.months, deposits, summaries, accounts, categories]
  );

  // Reimbursable money: this period, and what's still owed back overall.
  const reimbPeriod = useMemo(() => reimbursableActivity(bankEntries, result.months), [bankEntries, result.months]);
  const reimbAllTime = useMemo(() => reimbursableActivity(bankEntries), [bankEntries]);
  const dupGroups = useMemo(() => findDuplicateGroups(result.periodDeposits), [result.periodDeposits]);

  // Tech Daily Pay vs bank-statement payroll (reconciliation only).
  const usersById = useMemo(() => usersToMap(users), [users]);
  const laborFor = (months) => {
    const { startKey, endKey } = monthsRange(months);
    return startKey ? summarizeDailyLabor(laborDays, usersById, startKey, endKey).total : 0;
  };
  const periodDailyPay = useMemo(
    () => laborFor(result.months),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [laborDays, usersById, result.months]
  );
  const periodBankPayroll = useMemo(() => payrollFromCategoryTotals(result.categoryTotals), [result.categoryTotals]);
  const payrollDiff = Math.round((periodBankPayroll - periodDailyPay) * 100) / 100;

  // Paid invoices in this period with no payment recorded (not counted).
  const unpaidPaid = useMemo(
    () => paidInvoicesWithoutDeposit(invoices, deposits, result.months),
    [invoices, deposits, result.months]
  );

  const alreadyRecorded = payoutHistory.filter((h) => h.periodKey === periodKey(period));
  const participantsDirty = JSON.stringify(participants) !== savedParticipants;

  const problems = [];
  if (result.noAccounts) problems.push("No business bank accounts are set up. Add them on Accounting → Bank Statement → Statement Entries.");
  if (result.monthsWithNoSummaryAtAll.length)
    problems.push(`No bank statement information entered for ${result.monthsWithNoSummaryAtAll.map(monthShort).join(", ")}.`);
  const partialMissing = result.missing.filter((m) => !result.monthsWithNoSummaryAtAll.includes(m.month));
  if (partialMissing.length)
    problems.push(`Missing: ${partialMissing.map((m) => `${accountLabel(m.account)} (${monthShort(m.month)})`).join(", ")}.`);
  const cautions = [];
  if (dupGroups.length) cautions.push(`${dupGroups.length} possible duplicate payment${dupGroups.length === 1 ? "" : "s"} in this period. Review them on Deposits first, or profit will be too high.`);
  if (result.totals.uncategorized > 0)
    cautions.push(`${formatMoney(result.totals.uncategorized)} of money out isn't broken down and is being counted as a business expense.`);
  if (unpaidPaid.list.length)
    cautions.push(
      `${unpaidPaid.list.length} invoice${unpaidPaid.list.length === 1 ? " is" : "s are"} marked Paid in this period (${formatMoney(unpaidPaid.total)}) with no payment on Deposits, so ${unpaidPaid.list.length === 1 ? "it isn't" : "they aren't"} in money in. Record the payment with the Paid button on Approved Invoices or on Deposits: ${unpaidPaid.list.slice(0, 6).map((i) => `#${i.invoiceNumber || i.wo || i.id}${i.siteName || i.propertyName ? ` ${i.siteName || i.propertyName}` : ""}`).join(", ")}${unpaidPaid.list.length > 6 ? "…" : ""}.`
    );
  if (payouts.totalPercent > 100) cautions.push(`Percentages add up to ${payouts.totalPercent}%, which is more than 100%.`);

  /* --------------------------- participants --------------------------- */

  const addParticipant = () => {
    const u = users.find((x) => x.id === addUserId);
    if (!u) return;
    setParticipants((p) => [...p, { userId: u.id, name: userName(u), percent: "" }]);
    setAddUserId("");
  };
  const updatePercent = (userId, percent) =>
    setParticipants((p) => p.map((x) => (x.userId === userId ? { ...x, percent } : x)));
  const removeParticipant = (userId) => setParticipants((p) => p.filter((x) => x.userId !== userId));

  const saveParticipants = async () => {
    setBusy(true);
    try {
      const clean = participants.map((p) => ({ userId: p.userId, name: p.name, percent: Number(p.percent) || 0 }));
      await setDoc(doc(db, "appSettings", "profitSharing"), { participants: clean, updatedAt: serverTimestamp() }, { merge: true });
      setParticipants(clean);
      setSavedParticipants(JSON.stringify(clean));
    } catch (e) {
      console.error(e);
      alert("Could not save the profit sharing list.");
    } finally {
      setBusy(false);
    }
  };

  /* ------------------------------ record ------------------------------ */

  const recordPayout = async () => {
    if (!participants.length) {
      alert("Add at least one person first.");
      return;
    }
    const warnText = problems.length ? `\n\nWARNING — bank info is incomplete:\n• ${problems.join("\n• ")}` : "";
    const dupText = alreadyRecorded.length ? `\n\nA payout for ${periodLabel(period)} was already recorded.` : "";
    if (!window.confirm(`Record ${formatMoney(payouts.totalPayout)} in profit sharing for ${periodLabel(period)}?${warnText}${dupText}`)) return;
    setBusy(true);
    try {
      const snapshot = {
        periodKey: periodKey(period),
        periodLabel: periodLabel(period),
        period,
        paymentsReceived: result.paymentsReceived,
        businessOut: result.totals.businessOut,
        ownerDraws: result.totals.owner_draw,
        cashProfit: result.cashProfit,
        bankPayroll: periodBankPayroll,
        techDailyPay: periodDailyPay,
        participants: payouts.rows.map((r) => ({ userId: r.userId, name: r.name, percent: r.percent, payout: r.payout })),
        totalPayout: payouts.totalPayout,
        incompleteBankInfo: problems.length > 0,
        warnings: [...problems, ...cautions],
        createdAt: serverTimestamp(),
      };
      const ref = await addDoc(collection(db, "profitSharingPayouts"), snapshot);
      setPayoutHistory((h) => [{ id: ref.id, ...snapshot, createdAt: new Date() }, ...h]);
    } catch (e) {
      console.error(e);
      alert("Could not record the payout.");
    } finally {
      setBusy(false);
    }
  };

  const deletePayout = async (h) => {
    if (!window.confirm(`Delete the recorded payout for ${h.periodLabel}?`)) return;
    try {
      await deleteDoc(doc(db, "profitSharingPayouts", h.id));
      setPayoutHistory((prev) => prev.filter((x) => x.id !== h.id));
    } catch (e) {
      console.error(e);
      alert("Could not delete.");
    }
  };

  const downloadExcel = () => {
    const rows = [
      [`Priority Pest Defense — Profit Sharing — ${periodLabel(period)}`],
      problems.length ? ["WARNING: bank statement information incomplete", problems.join(" ")] : [],
      [],
      ["Payments received", result.paymentsReceived],
      ["Business money out", result.totals.businessOut],
      ["Cash profit", result.cashProfit],
      ["Owner draws (not counted)", result.totals.owner_draw],
      ["Payroll on bank statements (inside business money out)", periodBankPayroll],
      ["Tech Daily Pay recorded (check only, not subtracted)", periodDailyPay],
      [],
      ["Name", "Percent", "Payout"],
      ...payouts.rows.map((r) => [r.name, r.percent / 100, r.payout]),
      ["Total", payouts.totalPercent / 100, payouts.totalPayout],
      ["Kept by company", "", payouts.retained],
      [],
      ["Money out by category", "Counts as", "Amount"],
      ...result.categoryTotals.map((c) => [c.category, KINDS[c.kind]?.short || c.kind, c.amount]),
      ["Not broken down", "Business", result.totals.uncategorized],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 34 }, { wch: 16 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Profit Sharing");
    XLSX.writeFile(wb, `Profit_Sharing_${periodKey(period)}.xlsx`);
  };

  const availableUsers = users.filter((u) => !participants.some((p) => p.userId === u.id));
  const years = Array.from({ length: 6 }, (_, i) => now.getFullYear() - i);

  /* ------------------------------ render ------------------------------ */

  return (
    <div className="fin-page">
      <header className="fin-header">
        <div>
          <h1 className="fin-title">Profit &amp; Profit Sharing</h1>
          <p className="fin-subtitle">Money in, money out, profit and profit sharing for any month, quarter or year.</p>
        </div>
        <div className="fin-header__actions">
          <div className="fin-segment">
            <button className={periodType === "month" ? "is-active" : ""} onClick={() => setPeriodType("month")}>Month</button>
            <button className={periodType === "quarter" ? "is-active" : ""} onClick={() => setPeriodType("quarter")}>Quarter</button>
            <button className={periodType === "year" ? "is-active" : ""} onClick={() => setPeriodType("year")}>Year</button>
          </div>
          {periodType === "month" ? (
            <input className="fin-input" type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
          ) : (
            <>
              {periodType === "quarter" && (
                <select className="fin-input" value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}>
                  {[1, 2, 3, 4].map((q) => <option key={q} value={q}>Q{q}</option>)}
                </select>
              )}
              <select className="fin-input" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </>
          )}
          <button className="fin-btn fin-btn--secondary" onClick={downloadExcel} disabled={loading}>Download Excel</button>
        </div>
      </header>

      {!loading && problems.length > 0 && (
        <div className="fin-alert fin-alert--bad">
          <div className="fin-alert__icon" aria-hidden>!</div>
          <div className="fin-alert__body">
            <strong>Bank statement information is missing for {periodLabel(period)}. These payouts are not reliable yet.</strong>
            {problems.map((p) => <span key={p}>{p}</span>)}
            <span>Upload the statement on Accounting → Bank Statement → Statement Entries, or type the totals on Accounting → Bank Statement → Monthly Summary.</span>
          </div>
        </div>
      )}
      {!loading && cautions.length > 0 && (
        <div className="fin-alert">
          <div className="fin-alert__icon" aria-hidden>!</div>
          <div className="fin-alert__body">
            {cautions.map((c) => <span key={c}>{c}</span>)}
          </div>
        </div>
      )}

      <section className="fin-stats fin-stats--5">
        <div className="fin-stat">
          <span className="fin-stat__label">Money in (payments)</span>
          <span className="fin-stat__value">{formatMoney(result.paymentsReceived)}</span>
          <span className="fin-stat__sub">{result.paymentCount} payments · Deposits (incl. invoices paid)</span>
        </div>
        <div className="fin-stat">
          <span className="fin-stat__label">Business money out</span>
          <span className="fin-stat__value">− {formatMoney(result.totals.businessOut)}</span>
          <span className="fin-stat__sub">{result.summaryCount} bank month{result.summaryCount === 1 ? "" : "s"} entered</span>
        </div>
        <div className={`fin-stat fin-stat--hero ${result.cashProfit < 0 ? "is-neg" : ""}`}>
          <span className="fin-stat__label">Cash profit</span>
          <span className="fin-stat__value">{formatMoney(result.cashProfit)}</span>
          <span className="fin-stat__sub">{periodLabel(period)}</span>
        </div>
        <div className="fin-stat">
          <span className="fin-stat__label">Personal draws</span>
          <span className="fin-stat__value">{formatMoney(result.totals.owner_draw)}</span>
          <span className="fin-stat__sub">owner · not counted</span>
        </div>
        <div className="fin-stat">
          <span className="fin-stat__label">Still owed back</span>
          <span className="fin-stat__value">{formatMoney(reimbAllTime.balance)}</span>
          <span className="fin-stat__sub">reimbursable money in, all time</span>
        </div>
      </section>

      <section className="fin-card fin-card--spaced-b">
        <div className="fin-card__head">
          <div>
            <h2 className="fin-card__title">Payroll check — Tech Daily Pay</h2>
            <p className="fin-small fin-muted" style={{ margin: "2px 0 0" }}>
              Tech Daily Pay recorded for this period: <b>{formatMoney(periodDailyPay)}</b> vs payroll on bank statements:{" "}
              <b>{formatMoney(periodBankPayroll)}</b>
              {loading ? "" : Math.abs(payrollDiff) < 1 ? " — they match." : ` — difference ${formatMoney(Math.abs(payrollDiff))} (${payrollDiff > 0 ? "bank payroll is higher" : "Tech Daily Pay is higher"}).`}
            </p>
            <p className="fin-small fin-muted" style={{ margin: "4px 0 0" }}>
              Profit already takes payroll out through the bank statement lines, so Tech Daily Pay is shown here only to
              reconcile. It is not subtracted again. Differences are normal when payroll is paid the week after the work,
              or when payroll taxes / owner pay go through the same account.
            </p>
          </div>
        </div>
      </section>

      {monthly.length > 1 && (
        <section className="fin-card fin-card--spaced-b">
          <div className="fin-card__head">
            <h2 className="fin-card__title">Month by month</h2>
          </div>
          <div className="fin-table-wrap">
            <table className="fin-table fin-table--compact">
              <thead>
                <tr>
                  <th>Month</th>
                  <th className="fin-num">Money in</th>
                  <th className="fin-num">Business out</th>
                  <th className="fin-num">Profit</th>
                  <th className="fin-num">Personal draws</th>
                  <th className="fin-num">Reimbursable in</th>
                  <th className="fin-num">Paid back</th>
                  <th className="fin-num" title="Payroll lines on the bank statements (already inside Business out)">Bank payroll</th>
                  <th className="fin-num" title="Tech Daily Pay recorded — check only, not subtracted">Tech Daily Pay</th>
                  <th>Bank statements</th>
                </tr>
              </thead>
              <tbody>
                {monthly.map(({ month: m, r, bankOk, future }) => (
                  <tr key={m}>
                    <td className="fin-strong">{monthShort(m)}</td>
                    <td className="fin-num">{formatMoney(r.paymentsReceived)}</td>
                    <td className="fin-num">{formatMoney(r.totals.businessOut)}</td>
                    <td className={`fin-num fin-strong ${r.cashProfit < 0 ? "fin-neg" : ""}`}>{formatMoney(r.cashProfit)}</td>
                    <td className="fin-num">{formatMoney(r.totals.owner_draw)}</td>
                    <td className="fin-num">{formatMoney(r.totals.reimbursableIn)}</td>
                    <td className="fin-num">{formatMoney(r.totals.repayment)}</td>
                    <td className="fin-num">{formatMoney(payrollFromCategoryTotals(r.categoryTotals))}</td>
                    <td className="fin-num fin-muted">{formatMoney(laborFor([m]))}</td>
                    <td>
                      {future ? <span className="fin-muted fin-small">Not due yet</span> : bankOk ? <span className="fin-tag fin-tag--bank">Complete</span> : <span className="fin-tag fin-tag--missing">Missing</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="fin-strong">Total</td>
                  <td className="fin-num fin-strong">{formatMoney(result.paymentsReceived)}</td>
                  <td className="fin-num fin-strong">{formatMoney(result.totals.businessOut)}</td>
                  <td className={`fin-num fin-strong ${result.cashProfit < 0 ? "fin-neg" : ""}`}>{formatMoney(result.cashProfit)}</td>
                  <td className="fin-num fin-strong">{formatMoney(result.totals.owner_draw)}</td>
                  <td className="fin-num fin-strong">{formatMoney(result.totals.reimbursableIn)}</td>
                  <td className="fin-num fin-strong">{formatMoney(result.totals.repayment)}</td>
                  <td className="fin-num fin-strong">{formatMoney(periodBankPayroll)}</td>
                  <td className="fin-num fin-strong fin-muted">{formatMoney(periodDailyPay)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      )}

      <div className="fin-grid-2">
        <section className="fin-card">
          <div className="fin-card__head">
            <h2 className="fin-card__title">Profit sharing</h2>
            {participantsDirty && (
              <button className="fin-btn fin-btn--secondary fin-btn--sm" onClick={saveParticipants} disabled={busy}>Save list</button>
            )}
          </div>
          <div className="fin-table-wrap">
            <table className="fin-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th className="fin-num">Percent</th>
                  <th className="fin-num">Payout</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {payouts.rows.length === 0 && (
                  <tr><td colSpan="4" className="fin-empty">Add the people who share in profit below.</td></tr>
                )}
                {payouts.rows.map((r) => (
                  <tr key={r.userId}>
                    <td className="fin-strong">{r.name}</td>
                    <td className="fin-num">
                      <span className="fin-pct">
                        <input
                          className="fin-input fin-input--sm"
                          type="number"
                          min="0"
                          max="100"
                          step="0.5"
                          value={participants.find((p) => p.userId === r.userId)?.percent ?? ""}
                          onChange={(e) => updatePercent(r.userId, e.target.value)}
                        />
                        %
                      </span>
                    </td>
                    <td className="fin-num fin-strong">{formatMoney(r.payout)}</td>
                    <td className="fin-right">
                      <button className="fin-icon-btn" onClick={() => removeParticipant(r.userId)} aria-label={`Remove ${r.name}`}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {payouts.rows.length > 0 && (
                <tfoot>
                  <tr>
                    <td className="fin-strong">Total</td>
                    <td className={`fin-num fin-strong ${payouts.totalPercent > 100 ? "fin-warn-inline" : ""}`}>{payouts.totalPercent}%</td>
                    <td className="fin-num fin-strong">{formatMoney(payouts.totalPayout)}</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td className="fin-muted">Kept by company</td>
                    <td></td>
                    <td className="fin-num fin-muted">{formatMoney(payouts.retained)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          <div className="fin-card__foot">
            <select className="fin-input" value={addUserId} onChange={(e) => setAddUserId(e.target.value)}>
              <option value="">Add employee…</option>
              {availableUsers.map((u) => <option key={u.id} value={u.id}>{userName(u)}</option>)}
            </select>
            <button className="fin-btn fin-btn--secondary fin-btn--sm" onClick={addParticipant} disabled={!addUserId}>Add</button>
            <div className="fin-spacer" />
            <button className="fin-btn fin-btn--primary" onClick={recordPayout} disabled={busy || loading || !participants.length}>
              Record payout
            </button>
          </div>
          {result.cashProfit <= 0 && participants.length > 0 && !loading && (
            <p className="fin-small fin-muted fin-pad">No profit this period, so payouts are $0.</p>
          )}
          {alreadyRecorded.length > 0 && (
            <p className="fin-small fin-warn-inline fin-pad">A payout for {periodLabel(period)} is already recorded (see history).</p>
          )}
        </section>

        <section className="fin-card">
          <div className="fin-card__head">
            <h2 className="fin-card__title">Where the money went</h2>
          </div>
          <div className="fin-table-wrap fin-table-wrap--tall">
            <table className="fin-table fin-table--compact">
              <thead>
                <tr><th>Category</th><th>Counts as</th><th className="fin-num">Amount</th></tr>
              </thead>
              <tbody>
                {result.categoryTotals.length === 0 && result.totals.uncategorized === 0 && (
                  <tr><td colSpan="3" className="fin-empty">No money out entered for this period.</td></tr>
                )}
                {result.categoryTotals.map((c) => (
                  <tr key={c.category} className={KINDS[c.kind]?.counts ? "" : "is-muted"}>
                    <td>{c.category}</td>
                    <td><span className={`fin-kind fin-kind--${c.kind}`}>{KINDS[c.kind]?.short}</span></td>
                    <td className="fin-num">{formatMoney(c.amount)}</td>
                  </tr>
                ))}
                {result.totals.uncategorized > 0 && (
                  <tr>
                    <td>Not broken down</td>
                    <td><span className="fin-kind fin-kind--business">Business</span></td>
                    <td className="fin-num">{formatMoney(result.totals.uncategorized)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {(reimbAllTime.received.length > 0 || reimbAllTime.repaid.length > 0) && (
        <section className="fin-card fin-card--spaced">
          <div className="fin-card__head">
            <div>
              <h2 className="fin-card__title">Reimbursable money</h2>
              <p className="fin-small fin-muted" style={{ margin: "2px 0 0" }}>
                Money that came in but has to be paid back. Not income, and paying it back isn't an expense.
              </p>
            </div>
            <div className="fin-owed">
              <span>Still owed back</span>
              <b>{formatMoney(reimbAllTime.balance)}</b>
            </div>
          </div>
          <div className="fin-table-wrap">
            <table className="fin-table fin-table--compact">
              <thead>
                <tr><th>Date</th><th>What</th><th>From / note</th><th className="fin-num">In</th><th className="fin-num">Paid back</th></tr>
              </thead>
              <tbody>
                {[...reimbPeriod.received.map((e) => ({ ...e, _k: "in" })), ...reimbPeriod.repaid.map((e) => ({ ...e, _k: "out" }))]
                  .sort((a, b) => String(a.date).localeCompare(String(b.date)))
                  .map((e, i) => (
                    <tr key={e.id || i}>
                      <td className="fin-nowrap">{formatDateMDY(String(e.date || "").slice(0, 10))}</td>
                      <td>{e.vendorOrPayer || e.description || "—"}</td>
                      <td>
                        {e._k === "in" ? (e.reimbursableFrom === "other" ? "Loan / other" : "Owner") : "Paying back"}
                        {e.reimbursableNote ? ` · ${e.reimbursableNote}` : ""}
                      </td>
                      <td className="fin-num">{e._k === "in" ? formatMoney(e.amount) : ""}</td>
                      <td className="fin-num">{e._k === "out" ? formatMoney(e.amount) : ""}</td>
                    </tr>
                  ))}
                {reimbPeriod.received.length + reimbPeriod.repaid.length === 0 && (
                  <tr><td colSpan="5" className="fin-empty">Nothing reimbursable in {periodLabel(period)}.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="fin-card fin-card--spaced">
        <div className="fin-card__head">
          <h2 className="fin-card__title">Recorded payouts</h2>
        </div>
        <div className="fin-table-wrap">
          <table className="fin-table">
            <thead>
              <tr>
                <th>Period</th>
                <th className="fin-num">Cash profit</th>
                <th>Paid to</th>
                <th className="fin-num">Total paid</th>
                <th>Recorded</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {payoutHistory.length === 0 && <tr><td colSpan="6" className="fin-empty">Nothing recorded yet.</td></tr>}
              {payoutHistory.map((h) => (
                <tr key={h.id}>
                  <td className="fin-strong">
                    {h.periodLabel}
                    {h.incompleteBankInfo && <span className="fin-dup-badge fin-dup-badge--static" style={{ marginLeft: 6 }}>Bank info incomplete</span>}
                  </td>
                  <td className="fin-num">{formatMoney(h.cashProfit)}</td>
                  <td>{(h.participants || []).map((p) => `${p.name} ${formatMoney(p.payout)}`).join(" · ")}</td>
                  <td className="fin-num fin-strong">{formatMoney(h.totalPayout)}</td>
                  <td className="fin-nowrap">
                    {h.createdAt?.toDate ? formatDateMDY(h.createdAt.toDate().toISOString().slice(0, 10)) : h.createdAt instanceof Date ? formatDateMDY(h.createdAt.toISOString().slice(0, 10)) : "—"}
                  </td>
                  <td className="fin-right">
                    <button className="fin-btn fin-btn--ghost-danger fin-btn--sm" onClick={() => deletePayout(h)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <p className="fin-small fin-muted fin-footnote">
        How it's worked out: payments received come from Deposits (including invoices paid with the Paid button on
        Approved Invoices, Job Estimate Spreadsheet payments and Bank Statement payments tied to a work order). An invoice
        marked Paid is counted once, through its deposit — never on its own. Business money out comes from each business account's uploaded statement lines for
        the month (or the typed Monthly Summary when nothing was uploaded): money out, minus lines marked Personal and
        transfers. Owner reimbursable counts as business. Accounts marked Personal aren't counted.
        Payroll is counted from the bank statement payroll lines; Tech Daily Pay is shown beside it only as a check.
        Payout = cash profit × percent, and is $0 when there's no profit.
      </p>
    </div>
  );
}
